import { FiBookOpen } from 'react-icons/fi';

export const articlesBasicsData = {
  courseId: 'articles-basics',
  courseType: 'grammar',
  title: 'Articles (a, an, the)',
  icon: FiBookOpen,
  overview: {
    impact: 'Correct article use affects your grammar score significantly',
    focus: 'When to use a/an, the, or no article',
    uses: 'All IELTS sections — especially Writing',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Indefinite Articles: A and An',
      content: `<p>Use <strong>a/an</strong> with singular countable nouns when mentioning something for the first time or talking about one of many.</p><p><strong>A</strong> before consonant sounds. <strong>An</strong> before vowel sounds.</p>`,
      examples: [
        {
          label: 'First mention',
          text: 'I saw a dog in the park.'
        },
        {
          label: 'One of many',
          text: 'She is a teacher.'
        },
        {
          label: 'Vowel sound',
          text: 'He ate an apple. It took an hour.'
        }
      ]
    },
    {
      title: 'Definite Article: The',
      content: `<p>Use <strong>the</strong> when both speaker and listener know which specific thing is meant.</p>`,
      points: [
        'Second mention: \'I saw a dog. The dog was friendly.\'',
        'Unique things: the sun, the internet, the government',
        'Superlatives: the best, the largest, the most important',
        'Specific: \'the book on the table\' (we know which one)'
      ]
    },
    {
      title: 'No Article (Zero Article)',
      content: `<p>Some nouns don't need an article:</p>`,
      points: [
        'Uncountable nouns in general: \'Water is essential.\' (NOT \'The water is essential.\')',
        'Plural nouns in general: \'Dogs are loyal.\' (NOT \'The dogs are loyal.\')',
        'Most countries, cities, languages: France, London, English',
        'Meals, sports, subjects: breakfast, football, mathematics'
      ],
      warning: 'This is the most common article mistake in IELTS writing. Over-using \'the\' with general statements.'
    }
  ],
  exercises: [
    {
      title: 'Fill in the Article',
      type: 'select',
      instructions: 'Choose a, an, the, or — (no article).',
      questions: [
        {
          text: 'She is ___ engineer.',
          options: [
            'an',
            'a',
            'the',
            '—'
          ],
          answer: 'an',
          explanation: 'Vowel sound + job description = \'an\'.'
        },
        {
          text: '___ education is important.',
          options: [
            '—',
            'The',
            'An',
            'A'
          ],
          answer: '—',
          explanation: 'General statement about education → no article.'
        },
        {
          text: 'I visited ___ Eiffel Tower.',
          options: [
            'the',
            'a',
            'an',
            '—'
          ],
          answer: 'the',
          explanation: 'Unique landmark → \'the\'.'
        },
        {
          text: 'Could you pass me ___ salt?',
          options: [
            'the',
            'a',
            '—',
            'an'
          ],
          answer: 'the',
          explanation: 'Both people can see the specific salt → \'the\'.'
        },
        {
          text: '___ children need love and support.',
          options: [
            '—',
            'The',
            'A',
            'An'
          ],
          answer: '—',
          explanation: 'Children in general → no article.'
        }
      ]
    }
  ],
  tips: [
    '\'A/an\' = first mention or one of many',
    '\'The\' = specific, known, or second mention',
    'No article = general statements with plurals/uncountables',
    'Watch for vowel SOUNDS, not letters: \'a university\' (yoo-) but \'an hour\' (silent h)'
  ],
  dailyPractice: [
    'Practice articles (a, an, the) concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'English Articles: a/an for indefinite singular countable nouns, the for specific/known referents, zero article for general statements. Critical for IELTS Writing band score.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...articlesBasicsData,
      title: articlesBasicsData.title + ' (Foundations)',
      overview: { ...articlesBasicsData.overview, duration: '15-20 minutes' },
      sections: articlesBasicsData.sections.slice(0, Math.max(2, Math.ceil(articlesBasicsData.sections.length / 2))),
      exercises: articlesBasicsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...articlesBasicsData,
      title: articlesBasicsData.title + ' (Advanced)',
      overview: { ...articlesBasicsData.overview, duration: '30-35 minutes' },
      sections: articlesBasicsData.sections,
      exercises: articlesBasicsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(articlesBasicsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return articlesBasicsData;
}
